import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, BarChart2, Calendar, ArrowUpDown, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import { usePlayer } from '../../context/PlayerContext';
import { useStats } from '../../hooks/useStats';
import { formatBattingStat, formatPitchingStat, ensureNumber } from '../../utils/calculations';
const PlayerStatsSummary = ({ playerData: propPlayerData }) => {
  const { selectedPlayerId, activeSeason, viewingAllSeasons, dataVersion, playerData: contextPlayerData } = usePlayer();
  const [showingSeasonStats, setShowingSeasonStats] = useState(true);

  // Use playerData from context, fallback to prop
  const playerData = contextPlayerData || propPlayerData;

  // Use the useStats hook to get properly calculated stats
  const {
    battingStats,
    pitchingStats: hookPitchingStats,
    loading,
    refreshStats,
    dataSource,
    diagnosticInfo
  } = useStats(selectedPlayerId, activeSeason, !showingSeasonStats, dataVersion, playerData);
  
  // Debug info
  console.log('PlayerStatsSummary - Stats Source:', {
    dataSource,
    showingSeasonStats,
    activeSeason: activeSeason?.id,
    viewingAllSeasons: !showingSeasonStats,
    battingStats: {
      games: battingStats?.games || 0,
      atBats: battingStats?.atBats || 0,
      hits: battingStats?.hits || 0,
      average: battingStats?.average || 0
    },
    diagnosticInfo
  });
  
  // Initialize showing season stats if we have a season selected
  useEffect(() => {
    setShowingSeasonStats(!viewingAllSeasons && activeSeason);
  }, [viewingAllSeasons, activeSeason]);
  
  // Helper to ensure numbers are valid
  const safeNumber = (value) => {
    return ensureNumber(value);
  };

  // Helper to calculate total bases
  const calculateTotalBases = (stats) => {
    if (!stats) return 0;
    
    const singles = safeNumber(stats.hits) - (
      safeNumber(stats.doubles) + 
      safeNumber(stats.triples) + 
      safeNumber(stats.homeRuns)
    );
    
    return Math.max(0, singles) + 
      (2 * safeNumber(stats.doubles)) + 
      (3 * safeNumber(stats.triples)) + 
      (4 * safeNumber(stats.homeRuns));
  };

  // Career (all-time) stats
  const careerBattingStats = {
    average: safeNumber(playerData?.battingStats?.average),
    homeRuns: safeNumber(playerData?.battingStats?.homeRuns),
    rbi: safeNumber(playerData?.battingStats?.rbi),
    hits: safeNumber(playerData?.battingStats?.hits),
    atBats: safeNumber(playerData?.battingStats?.atBats),
    walks: safeNumber(playerData?.battingStats?.walks),
    strikeouts: safeNumber(playerData?.battingStats?.strikeouts),
    games: safeNumber(playerData?.recentGames?.length) || 0,
    totalBases: safeNumber(playerData?.battingStats?.totalBases) || 
               calculateTotalBases(playerData?.battingStats)
  };
  
  const careerPitchingStats = {
    era: playerData?.pitchingStats?.era,
    strikeouts: playerData?.pitchingStats?.strikeouts,
    wins: playerData?.pitchingStats?.wins,
    inningsPitched: playerData?.pitchingStats?.inningsPitched,
    earnedRuns: playerData?.pitchingStats?.earnedRuns,
    games: playerData?.recentGames?.filter(g => g.pitchingStats)?.length || 0
  };
  
  // Season-specific stats
  const seasonBattingStats = playerData?.seasonStats?.batting || {
    average: 0,
    homeRuns: 0,
    rbi: 0,
    hits: 0,
    atBats: 0,
    walks: 0,
    strikeouts: 0,
    totalBases: 0,
    games: safeNumber(playerData?.seasonGames?.length) || 0
  };
  
  const seasonPitchingStats = playerData?.seasonStats?.pitching || {
    era: 0,
    strikeouts: 0,
    wins: 0,
    inningsPitched: 0,
    earnedRuns: 0,
    games: playerData?.seasonGames?.filter(g => g.pitchingStats)?.length || 0
  };
  
  // Convert useStats hook data for comparison with career stats
  const currentBattingStats = battingStats;
  const currentPitchingStats = hookPitchingStats;
  
  // Handle manual refresh click
  const handleRefreshClick = () => {
    refreshStats();
  };

  // Helper function to compare career vs season stats
  const getComparisonIndicator = (key, seasonValue, careerValue) => {
    if (seasonValue === undefined || careerValue === undefined) return null;
    if (seasonValue === 0 && careerValue === 0) return null;
    
    // For ERA, lower is better
    if (key === 'era') {
      if (seasonValue < careerValue) return <ChevronUp className="text-green-500" size={14} />;
      if (seasonValue > careerValue) return <ChevronDown className="text-red-500" size={14} />;
    } else {
      // For all other stats, higher is better
      if (seasonValue > careerValue) return <ChevronUp className="text-green-500" size={14} />;
      if (seasonValue < careerValue) return <ChevronDown className="text-red-500" size={14} />;
    }
    return null;
  };

  const formatStatValue = (key, value) => {
    if (value === undefined || value === null || isNaN(value)) return '-';
    
    if (key === 'average' || key === 'obp' || key === 'slg' || key === 'ops') {
      return formatBattingStat(value);
    }
    if (key === 'era' || key === 'whip') {
      return formatPitchingStat(value);
    }
    if (key === 'inningsPitched') {
      return value.toFixed(1);
    }
    return value;
  };

  const getStatLabel = (key) => {
    const labels = {
      average: 'Batting Average',
      homeRuns: 'Home Runs',
      rbi: 'RBI',
      hits: 'Hits',
      atBats: 'At Bats',
      walks: 'Walks',
      strikeouts: 'Strikeouts',
      games: 'Games',
      era: 'ERA',
      whip: 'WHIP',
      wins: 'Wins',
      losses: 'Losses',
      inningsPitched: 'Innings Pitched',
      earnedRuns: 'Earned Runs',
      saves: 'Saves',
      obp: 'On-Base %',
      slg: 'Slugging %',
      ops: 'OPS',
      totalBases: 'Total Bases'
    };
    return labels[key] || key;
  };

  return (
    <div className="bg-white dark:bg-dark-200 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-400">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg dark:text-darkText-primary flex items-center gap-2">
          <BarChart2 size={20} className="text-blue-600 dark:text-blue-400" />
          Statistics Summary
        </h3>
        
        <div className="flex items-center space-x-2">
          {/* Refresh Button */}
          <button 
            onClick={handleRefreshClick} 
            className="flex items-center px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 dark:text-blue-300 rounded-md transition-colors"
            disabled={loading}
            title="Refresh statistics"
          >
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-xs">Refresh</span>
          </button>
          
          {/* Stats toggle - season vs career */}
          {activeSeason && (
            <div className="flex items-center">
              <button
                onClick={() => setShowingSeasonStats(!showingSeasonStats)}
                className={`flex items-center px-3 py-1 rounded-md text-xs transition ${
                  showingSeasonStats 
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                }`}
              >
                <ArrowUpDown size={12} className="mr-1" />
                {showingSeasonStats ? 'Season Stats' : 'Career Stats'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Loading indicator */}
      {loading && (
        <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-100 dark:border-blue-800/30 rounded-md text-sm">
          <div className="flex items-center">
            <RefreshCw size={14} className="animate-spin mr-2" />
            <span>Refreshing statistics...</span>
          </div>
        </div>
      )}
      
      {/* Season Context Banner */}
      {activeSeason && (
        <div className={`mb-4 p-2 flex items-center rounded-md ${
          showingSeasonStats
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
            : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
        }`}>
          <Calendar size={16} className="mr-2 flex-shrink-0" />
          <span className="text-sm">
            {showingSeasonStats
              ? `${activeSeason.name} (${activeSeason.year}) - ${currentBattingStats.games || 0} Games Played`
              : `Career Stats - ${careerBattingStats.games || 0} Total Games`
            }
          </span>
        </div>
      )}

      {/* Career Highlights - Only show when viewing career stats */}
      {!showingSeasonStats && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800/30 rounded-lg">
          <h4 className="font-semibold text-purple-800 dark:text-purple-400 mb-3 flex items-center gap-2">
            <BarChart2 size={16} />
            Career Highlights
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {formatBattingStat(careerBattingStats.average)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Career AVG</div>
            </div>
            <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {careerBattingStats.homeRuns}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Home Runs</div>
            </div>
            <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {careerBattingStats.rbi}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">RBI</div>
            </div>
            <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {careerBattingStats.hits}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Total Hits</div>
            </div>
          </div>

          {/* Pitching highlights if applicable */}
          {careerPitchingStats && careerPitchingStats.games > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
                <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                  {formatPitchingStat(careerPitchingStats.era)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Career ERA</div>
              </div>
              <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
                <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                  {formatPitchingStat(careerPitchingStats.whip)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Career WHIP</div>
              </div>
              <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
                <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                  {careerPitchingStats.wins}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Wins</div>
              </div>
              <div className="bg-white dark:bg-dark-200 p-3 rounded-md shadow-sm">
                <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                  {careerPitchingStats.strikeouts}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Strikeouts</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Batting Stats */}
      <div className="mb-6">
        <h4 className="font-medium text-blue-800 dark:text-blue-400 mb-3 flex items-center gap-2">
          <Target size={16} />
          Batting Stats {!showingSeasonStats && <span className="text-xs font-normal text-gray-500">(Career Totals)</span>}
        </h4>
        <div className="space-y-3">
          {/* Ensure these specific stats are displayed in this order */}
          {['average', 'obp', 'slg', 'ops', 'rbi', 'homeRuns', 'hits', 'totalBases'].map((key) => {
            const value = currentBattingStats[key];
            const careerValue = careerBattingStats[key];
            return (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-darkText-secondary">
                    {getStatLabel(key)}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="font-medium dark:text-darkText-primary">
                      {formatStatValue(key, value)}
                    </span>

                    {/* Show comparison with career when viewing season stats */}
                    {showingSeasonStats && activeSeason && careerValue !== undefined && (
                      <div className="flex items-center gap-1">
                        {getComparisonIndicator(key, value, careerValue)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          vs {formatStatValue(key, careerValue)} career
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress bar for batting average */}
                {key === 'average' && value > 0 && (
                  <div className="w-full bg-gray-200 dark:bg-dark-400 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full"
                      style={{ width: `${Math.min(value * 100, 100)}%` }}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* On Pace Section - Only show for season stats */}
      {showingSeasonStats && activeSeason && currentBattingStats.games > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-green-800 dark:text-green-400 mb-3 flex items-center gap-2">
            <TrendingUp size={16} />
            On Pace For
          </h4>
          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800/30 rounded-md shadow-sm">
            {/* Calculate on-pace stats based on games played and estimated season length */}
            {(() => {
              // Use the totalGames value from the season, or fall back to scheduled games or default
              const seasonTotalGames = activeSeason?.totalGames || playerData?.scheduledGames?.length || 162;
              const gamesPlayed = currentBattingStats.games || 0;
              
              // Only show if we have games played
              if (gamesPlayed === 0) return null;
              
              // Calculate pace multiplier
              const paceMultiplier = seasonTotalGames / gamesPlayed;
              
              // Stats to project - Counting stats
              const countingStats = [
                { 
                  label: 'Hits', 
                  value: Math.round(currentBattingStats.hits * paceMultiplier),
                  current: currentBattingStats.hits
                },
                { 
                  label: 'Home Runs', 
                  value: Math.round(currentBattingStats.homeRuns * paceMultiplier),
                  current: currentBattingStats.homeRuns
                },
                { 
                  label: 'RBI', 
                  value: Math.round(currentBattingStats.rbi * paceMultiplier),
                  current: currentBattingStats.rbi
                },
                { 
                  label: 'Runs', 
                  value: Math.round(currentBattingStats.runs * paceMultiplier),
                  current: currentBattingStats.runs
                },
                { 
                  label: 'Strikeouts', 
                  value: Math.round(currentBattingStats.strikeouts * paceMultiplier),
                  current: currentBattingStats.strikeouts
                }
              ];
              
              // Rate stats - these don't use the multiplier directly
              const rateStats = [
                {
                  label: 'AVG',
                  value: currentBattingStats.average,
                  format: (val) => formatBattingStat(val)
                },
                {
                  label: 'OBP',
                  value: currentBattingStats.obp,
                  format: (val) => formatBattingStat(val)
                },
                {
                  label: 'SLG',
                  value: currentBattingStats.slg,
                  format: (val) => formatBattingStat(val)
                }
              ];
              
              return (
                <>
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 border-b border-green-200 dark:border-green-800/30 pb-1">
                      Season Totals
                    </h5>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {countingStats.map((stat, index) => (
                        <div key={index} className="bg-white dark:bg-dark-200 p-2 rounded-md shadow-sm border border-green-100 dark:border-green-900/20">
                          <div className="text-lg font-bold text-green-700 dark:text-green-400 text-center">
                            {stat.value}
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-500 text-center">
                            {stat.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                            Current: {stat.current}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2 border-b border-blue-200 dark:border-blue-800/30 pb-1">
                      Rate Statistics
                    </h5>
                    <div className="grid grid-cols-3 gap-3">
                      {rateStats.map((stat, index) => (
                        <div key={index} className="bg-white dark:bg-dark-200 p-2 rounded-md shadow-sm border border-blue-100 dark:border-blue-900/20">
                          <div className="text-lg font-bold text-blue-700 dark:text-blue-400 text-center">
                            {stat.format(stat.value)}
                          </div>
                          <div className="text-xs text-blue-600 dark:text-blue-500 text-center">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 text-center">
                    Projections based on {currentBattingStats.games} games played of {seasonTotalGames} game season
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {/* Pitching Stats */}
      {currentPitchingStats && (currentPitchingStats.inningsPitched > 0 || currentPitchingStats.games > 0 || currentPitchingStats.strikeouts > 0) ? (
        <div className="mb-6">
          <h4 className="font-medium text-purple-800 dark:text-purple-400 mb-3 flex items-center gap-2">
            <Target size={16} />
            Pitching Stats
          </h4>
          <div className="space-y-3">
            {Object.entries(currentPitchingStats)
              .filter(([key]) => key !== 'games' && ['era', 'whip', 'wins', 'inningsPitched', 'strikeouts', 'earnedRuns'].includes(key))
              .map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-darkText-secondary">
                      {getStatLabel(key)}
                    </span>
                    <div className="flex items-center">
                      <span className="font-medium dark:text-darkText-primary">
                        {formatStatValue(key, value)}
                      </span>
                      
                      {/* Show comparison indicator when viewing season stats */}
                      {showingSeasonStats && activeSeason && careerPitchingStats[key] && (
                        <span className="ml-2">
                          {getComparisonIndicator(key, value, careerPitchingStats[key])}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress bar for ERA (inverse - lower is better) */}
                  {key === 'era' && value > 0 && (
                    <div className="w-full bg-gray-200 dark:bg-dark-400 rounded-full h-1.5">
                      <div 
                        className="bg-purple-600 dark:bg-purple-500 h-1.5 rounded-full" 
                        style={{ width: `${Math.max(0, 100 - Math.min(value * 10, 100))}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h4 className="font-medium text-purple-800 dark:text-purple-400 mb-3 flex items-center gap-2">
            <Target size={16} />
            Pitching Stats
          </h4>
          <div className="p-4 text-center bg-gray-50 dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-400">
            <p className="text-gray-600 dark:text-darkText-secondary">No pitching statistics available</p>
          </div>
        </div>
      )}

      {/* Recent Performance Trends */}
      <div className="mt-6">
        <h4 className="font-medium text-gray-700 dark:text-darkText-secondary mb-3 flex items-center gap-2">
          <TrendingUp size={16} />
          Recent Performance
        </h4>
        <div className="space-y-2">
          {/* Show season-specific games if viewing season stats and season games exist */}
          {showingSeasonStats && playerData?.seasonGames && playerData.seasonGames.length > 0 ? (
            playerData.seasonGames.slice(0, 3).map((game, index) => (
              <div key={index} className="border border-gray-200 dark:border-dark-400 p-2 rounded text-sm">
                <div className="flex justify-between">
                  <span className="font-medium dark:text-darkText-primary">{game.opponent || 'Opponent'}</span>
                  <span className="text-gray-500 dark:text-darkText-tertiary">{game.date}</span>
                </div>
                <div className="mt-1 text-sm text-gray-600 dark:text-darkText-secondary">
                  {game.battingStats && (
                    <span>
                      {game.battingStats.hits || 0}-{game.battingStats.atBats || 0}
                      {game.battingStats.atBats > 0 && ` (${(game.battingStats.hits / game.battingStats.atBats).toFixed(3)})`}
                    </span>
                  )}
                  {game.pitchingStats && (
                    <span className="ml-2">
                      {game.pitchingStats.inningsPitched || 0} IP, {game.pitchingStats.strikeouts || 0} K
                    </span>
                  )}
                </div>
                {/* Season indicator */}
                {activeSeason && (
                  <div className="mt-1 flex items-center">
                    <Calendar size={12} className="text-blue-500 mr-1" />
                    <span className="text-xs text-blue-600 dark:text-blue-400">{activeSeason.name}</span>
                  </div>
                )}
              </div>
            ))
          ) : playerData?.recentGames && playerData.recentGames.length > 0 ? (
            // Show all games when viewing career stats
            playerData.recentGames.slice(0, 3).map((game, index) => (
              <div key={index} className="border border-gray-200 dark:border-dark-400 p-2 rounded text-sm">
                <div className="flex justify-between">
                  <span className="font-medium dark:text-darkText-primary">{game.opponent || 'Opponent'}</span>
                  <span className="text-gray-500 dark:text-darkText-tertiary">{game.date}</span>
                </div>
                <div className="mt-1 text-sm text-gray-600 dark:text-darkText-secondary">
                  {game.battingStats && (
                    <span>
                      {game.battingStats.hits || 0}-{game.battingStats.atBats || 0}
                      {game.battingStats.atBats > 0 && ` (${(game.battingStats.hits / game.battingStats.atBats).toFixed(3)})`}
                    </span>
                  )}
                  {game.pitchingStats && (
                    <span className="ml-2">
                      {game.pitchingStats.inningsPitched || 0} IP, {game.pitchingStats.strikeouts || 0} K
                    </span>
                  )}
                </div>
                {/* Show season tag if exists */}
                {game.seasonId && (
                  <div className="mt-1 flex items-center">
                    <Calendar size={12} className="text-purple-500 mr-1" />
                    <span className="text-xs text-purple-600 dark:text-purple-400">Season game</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-darkText-tertiary text-sm">No recent games</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsSummary;
